<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import { useDragAndDrop } from '@formkit/drag-and-drop/vue';
import { animations } from '@formkit/drag-and-drop';
import { FormModel } from '@/models';
import { useFormStore } from '@/stores/forms';
import { CustomButton, ButtonType, FormOrderRow } from '@/components';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const formsStore = useFormStore();
const [parent, forms] = useDragAndDrop(formsStore.formList,
  {
    plugins: [
      animations()]
  }
);
const initialForms = ref<FormModel[]>(JSON.parse(JSON.stringify(formsStore.formList)));

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
};

const saveOrder = async () => {
  forms.value.forEach((form, index) => {
    form.order = index + 1;
    form.enforcements.order = index + 1;
  });

  await formsStore.updateFormsOrder(forms.value);
  emits('onClose', true);
};

const onDragEnd = () => {
  forms.value.forEach((form, index) => {
    form.order = index + 1;
    form.enforcements.order = index + 1;
  });
};

const isPristine = computed(() => {
  const oldValue = JSON.parse(JSON.stringify(initialForms.value));
  const newValue = JSON.parse(JSON.stringify(forms.value));
  return JSON.stringify(oldValue) !== JSON.stringify(newValue);
});
</script>

<template>
  <div>
    <Dialog :visible="visible"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <template #container="{}">
        <div class="content-root">
          <div>
            <h2>Form Order</h2>
          </div>
          <div class="description">
            Drag forms to set order that they should appear when entering data.
          </div>
          <ul ref="parent">
            <li v-for="form in forms"
                :key="form.identifier"
                class="row"
                @dragend="onDragEnd">
              <FormOrderRow :form="form" />
            </li>
          </ul>
        </div>
        <div class="footer">
          <div class="empty"></div>
          <div class="footer-buttons">
            <CustomButton text="Cancel"
              @onClick="closeDialog"
              :margin-left="16"
              :type="ButtonType.OUTLINED"/>
            <CustomButton text="Save"
              @onClick="saveOrder"
              :disabled="!isPristine"
              :margin-left="16"
              :loading="formsStore.formsOrderLoader.isLoading"
              :type="ButtonType.POSITIVE"/>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../assets/dialog";
.content-root {
  width: 1200px;
  height: 80vh;
  padding: 40px 28px;
}
h2 {
  font-weight: 700;
  line-height: 32px;
  padding: 0 12px;
}
.description {
  padding: 0px 12px;
  font-weight: 600;
}
ul {
  list-style: none;
  padding: 12px;
  height: 100%;
  overflow: auto;
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;

  .footer-buttons {
    display: flex;
  }
}
.row {
    padding: 8px 0;
}
</style>
